import {useNavigate, useParams} from "react-router-dom";
import {
    Button,
    HStack,
    IconButton,
    Input,
    Spinner,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Text,
    useToast,
    VStack
} from "@chakra-ui/react";
import {AddIcon, ArrowBackIcon, CheckCircleIcon, DeleteIcon} from "@chakra-ui/icons";
import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {scrollbarCss} from "../../../../theme/theme";
import '../../../../../node_modules/react-quill/dist/quill.snow.css';
import "../../../../../node_modules/react-datepicker/dist/react-datepicker.css";
import ReactQuill from "react-quill";
import {CategoriesResponseNew, CategorySearchModel} from "../../../../data/model/Category";
import {useQuery} from "@tanstack/react-query";
import {
    addMediaNew,
    addProduct,
    deleteMediaNew,
    deleteVariantNew,
    editProductNew,
    editVariantNew,
    getCategoriesAllNew,
    getProducersMinify,
    getProductsById
} from "../../../../data/api/Customer";
import {Resolver, useForm} from "react-hook-form";
import {
    AttributeModelNew,
    FeaturedModel,
    ProductModelNew,
    ProductQueryParams,
    VariantModelNew
} from "../../../../data/model/Product";
import {yupResolver} from "@hookform/resolvers/yup";
import {editProductSchema, productSchema} from "../../../../data/schema/productSchema";
import {getData, IS_SMARTVENUES, QDRANT_BE_URL, toolbarOptions} from "../../../../utils/Utils";
import {Option} from "react-multi-select-component";
import {AddTableRow} from "../../../../components/atoms/AddTableRow/AddTableRow";
import {SetImageModel} from "../../../../data/model/Producer";
import {BaseResponse} from "../../../../data/model/BaseResponse";
import {ImageSelectorComponent} from "../../../../components/molecules/ImageSelectorComponent/ImageSelectorComponent";
import {MediaSelectorComponent} from "../../../../components/molecules/MediaSelectorComponent/MediaSelectorComponent";
import Select, {components, MultiValue, SingleValue} from "react-select";
import GlutenFreeLogo from "../../../../components/atoms/svg/gluten_free";
import VeganLogo from "../../../../components/atoms/svg/vegan_logo";
import BioLogo from "../../../../components/atoms/svg/bio_logo";
import Recycled from "../../../../components/atoms/svg/recycled";
import SicilyLogo from "../../../../components/atoms/svg/sicily_logo";

type OptionType = { value: string; label: string };

export const AddProductPage = () => {
    const navigate = useNavigate();
    const toast = useToast();

    const {id} = useParams();
    const userType = localStorage.getItem("userType");

    const productResponse = useQuery({
        queryKey: ["get-product", id],
        enabled: !!id,
        queryFn: () => getProductsById({id}),
        refetchOnWindowFocus: "always",
        staleTime: 0
    });

    const {data, refetch} = productResponse || {};
    const productData = data?.data as (BaseResponse<ProductModelNew>) || {};
    const product = productData.data;
    const {
        name: nameOld,
        sku: skuOld,
        barcode,
        description: descriptionOld,
        shortDescription: shortDescriptionOld,
    } = product || {};

    const methods = useForm<ProductQueryParams>({
        mode: "all",
        resolver: yupResolver(id ? editProductSchema : productSchema) as Resolver<ProductQueryParams>
    })

    const {
        register,
        handleSubmit,
        setValue,
        formState: {
            errors,
            isSubmitting
        }
    } = methods;

    const onSubmit = async (product: ProductQueryParams) => {
        try {
            const list: SetImageModel[] = [];

            // Popolare l'elenco delle immagini da caricare
            mainImage?.file && list.push(mainImage);
            images?.forEach(value => list.push(value));

            if (id) {
                // Costruzione delle liste delle varianti
                const variantsList = variants
                    .filter(value => typeof value.id === "number" && !value.isDeleted)
                    .map(value => value.value ?? "")
                    .filter(value => value !== "");

                const variantsListDelete = variants
                    .filter(value => typeof value.id === "string" && value.isDeleted)
                    .map(value => value.id)
                    .filter(value => id !== "");

                const variantsListUpdate = variants
                    .filter(value => typeof value.id === "string" && value.isEdited)
                    .filter(value => value.value !== "");

                // Modifica del prodotto
                const data = await editProductNew(id, {
                    ...product,
                    // inEvidence: switchState,
                    variants: variantsList
                });

                // Eliminazione, caricamento immagini e gestione varianti in parallelo
                await Promise.all([
                    ...imageToDelete.map(value => value !== "" && deleteMediaNew(value)), // Eliminazione immagini
                    ...list.map(value => value.file && addMediaNew({
                        type: "Product",
                        id: id || "",
                        isMain: value.isMain || false,
                    }, value.file)), // Aggiunta immagini
                    ...variantsListDelete.map(value => deleteVariantNew(value?.toString() ?? "")), // Eliminazione varianti
                    ...variantsListUpdate.map(value => editVariantNew(value.id?.toString() ?? "", {value: value.value})) // Modifica varianti
                ]);

                await fetch(`${QDRANT_BE_URL}/update/${id}?type=${IS_SMARTVENUES ? "smartvenues" : "enoteca"}`, {
                    method: "PUT",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({
                        product_id: id,
                        product_name: product?.name,
                        description: product?.description,
                        category: product?.categoryList?.toString(),
                        tags: product?.labels,
                        image_url: mainImage,
                    })
                });

                // Ricaricare i dati e navigare
                await refetch();
                navigate("/products", {replace: true});
            } else {
                // Creazione di un nuovo prodotto
                const variantsList = variants
                    .filter(value => !value.isDeleted)
                    .map(value => value.value ?? "")
                    .filter(value => value !== "");

                const data = await addProduct({
                    ...product,
                    variants: variantsList
                });

                const productNew = getData<ProductModelNew>(data);

                // Caricamento delle immagini associate
                const result = await Promise.all(
                    list.map(value => value.file && addMediaNew({
                        type: "Product",
                        id: productNew?.id || "",
                        isMain: value.isMain || false,
                    }, value.file))
                );

                let firstUrl: string | undefined = undefined;

                if (result && result.length > 0 && result[0]?.data?.data?.url) {
                    firstUrl = result[0].data.data.url;
                    console.log("Primo URL:", firstUrl);
                } else {
                    console.log("Nessun URL trovato");
                }

                await fetch(`${QDRANT_BE_URL}/create?type=${IS_SMARTVENUES ? "smartvenues" : "enoteca"}`, {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({
                        product_id: productNew?.id,
                        product_name: productNew?.name,
                        description: productNew?.description,
                        producer: productNew?.producer?.name,
                        category: productNew?.categories?.toString(),
                        tags: productNew?.labels,
                        image_url: firstUrl,
                    })
                });

                // Navigare alla lista prodotti
                navigate("/products", {replace: true});
            }
        } catch (error) {
            // Gestione degli errori
            toast({
                title: "Si è verificato un errore",
            });
        }
    };

    const responseAll = useQuery({
        queryKey: ['get-categories-all-all'],
        queryFn: () => getCategoriesAllNew(),
        refetchOnWindowFocus: "always"
    });

    const {data: dataCategoriesAll} = responseAll || {};

    const categoriesAllDataResponse = useMemo(() => dataCategoriesAll?.data as CategoriesResponseNew || {}, [dataCategoriesAll?.data]);
    const categoriesAllData = useMemo(() => categoriesAllDataResponse?.data || [], [categoriesAllDataResponse]);

    const {data: producersData} = useQuery({queryKey: ["producer-mine-minify"], queryFn: () => getProducersMinify()})
    const producers = getData<ProductModelNew[]>(producersData) || [];

    const [selectedLabels, setSelectedLabels] = useState<OptionType[] | null>([]);

    const [chooseCategories, setChooseCategories] = useState<OptionType[]>([]);

    const [chooseProducer, setChooseProducer] = useState<Option | null>(null);
    const [attributes, setAttributes] = useState<KeyValueSectionMap>(new Map());
    const [variants, setVariants] = useState<VariantModelNew[]>([]);
    const [categoriesAll, setCategoriesAll] = useState((categoriesAllData || []).map((value) => {
        const model: CategorySearchModel = {
            id: value?.id,
            name: value?.name,
            isFromAll: true
        }
        return model;
    }));

    const handleProducerChange = (newValue: SingleValue<Option> | null) => {
        if (!newValue) {
            setChooseProducer(null);
            setValue("producerId", undefined);
        } else {
            setChooseProducer(newValue);
            setValue("producerId", newValue.value);
        }
    };

    const [mainImage, setMainImage] = useState<SetImageModel | undefined>()
    const [images, setImages] = useState<SetImageModel[]>([])

    const [imageToDelete, setImageToDelete] = useState<string[]>([]);

    const options = categoriesAll.map((value) => ({
        label: value.name || "",
        value: value.id || ""
    }));

    const producerOptions = producers.map((value) => ({
        label: value.name || "",
        value: value.id || ""
    })) || [];

    useEffect(() => {
        setCategoriesAll(categoriesAllData || []);
    }, [categoriesAll, categoriesAllData]);

    const [valueQuill, setValueQuill] = useState<string | undefined>(descriptionOld);
    const [valueShortQuill, setValueShortQuill] = useState<string | undefined>(shortDescriptionOld);

    const [switchState, setSwitchState] = useState<FeaturedModel | undefined>(product?.inEvidence);

    function addAttribute(key: string, value: KeyValueSectionModel) {
        setAttributes(new Map(attributes.set(key, value)));
    }

    function removeAttribute(key: string) {
        const newMap = new Map(attributes);
        newMap.delete(key);
        setAttributes(newMap);
    }

    function setNewAttribute(key: string, value: KeyValueSectionModel) {
        const newMap = new Map(attributes);
        newMap.set(key, value);
        setAttributes(newMap);
    }

    function addVariant(value: VariantModelNew) {
        setVariants([...variants, value]);
    }

    function updateVariant(id?: string | number, value?: AttributeModelNew) {
        setVariants(prevState => prevState.map(variant =>
            variant.id === id ? {...value, isEdited: true} : variant
        ));
    }

    function removeVariant(id?: string | number) {
        setVariants(prevState => prevState.map(variant =>
            variant.id === id ? {...variant, isDeleted: true} : variant
        ));
    }

    useEffect(() => {
        setValue("attributes", JSON.stringify(Array.from(attributes.values())));
    }, [attributes, setValue]);

    useEffect(() => {
        setValue("categoryList", chooseCategories.map(value => value.value));
    }, [variants, chooseCategories, setValue]);

    useEffect(() => {
        setValue("labels", selectedLabels?.map((value) => value.value)?.join(";"));
    }, [selectedLabels, setValue]);

    useEffect(() => {
        if (product) {
            setValueQuill(product.description);
            setValueShortQuill(product.shortDescription);

            const categories = product.categories?.map((value) => ({
                label: value.name || "",
                value: value.id || ""
            })) || [];

            setChooseCategories(categories);

            if ((product?.labels?.length || 0) > 0) {
                setSelectedLabels(product?.labels?.split(";")?.map(value => ({
                    label: getLabelNameByValue(value) || "-",
                    value: value
                })) || []);
            }

            setChooseProducer({
                label: product.producer?.name || "",
                value: product.id || ""
            } || {});

            const attributesOld: KeyValueSectionMap = transformToKeyValueSectionMap(product?.attributes?.toString() ?? "");

            if (attributes.size === 0) {
                setAttributes(attributesOld);
            }

            if (variants.length === 0) {
                setVariants(product?.variants ?? []);
            }

            const mainImage = product.mediaList?.find((value) => value.isMain);
            const otherMedia = product.mediaList?.filter((value) => !value.isMain) || [];

            if (mainImage?.url) {
                setMainImage({
                    imageUrl: mainImage?.url,
                    mediaType: mainImage.mediaType,
                    id: mainImage.id,
                    name: mainImage.name
                });
            }

            setSwitchState(product.inEvidence);

            setImages(otherMedia.map((value) => ({
                imageUrl: value.url,
                mediaType: value.mediaType,
                isMain: false,
                id: value.id,
                name: value.name
            })));
        }
    }, [attributes.size, product?.attributes, product?.categories, product?.labels, product?.description, product?.id, product?.mediaList, product?.shortDescription, product?.variants, variants.length, product?.inEvidence]);

    function getNameRow() {
        return <AddTableRow name={"Nome (*)"}>
            <VStack alignItems={"flex-start"} w={"100%"}>
                <Input
                    inputMode={"text"}
                    colorScheme={"primary"}
                    variant='filled'
                    _placeholder={{fontSize: 14}}
                    defaultValue={nameOld}
                    errorBorderColor={"error.700"}
                    disabled={isSubmitting}
                    isInvalid={!!errors.name}
                    focusBorderColor={!!errors.name ? "error.700" : "primary.400"}
                    {...register('name')}
                    placeholder='Es. Il mio prodotto'/>
                {!!errors.name && <Text fontSize={14}
                                        alignSelf={"start"}
                                        textColor={"error.700"}>
                    {errors.name && errors.name.message}
                </Text>}
                <Text fontSize={"14px"} color={"rgba(0,0,0,0.6)"}>Il nome rappresentativo del tuo
                    prodotto</Text>
            </VStack>
        </AddTableRow>;
    }

    // function getSwitchRow() {
    //     return <AddTableRow name={"In evidenza"}>
    //         <Button textColor={'white'} onClick={() => setSwitchState()}
    //                 backgroundColor={switchState ? "primary.400" : "grey"}>{switchState ? "In evidenza" : "Non in evidenza"}</Button>
    //     </AddTableRow>
    // }

    function getSkuRow() {
        return <AddTableRow name={"SKU"}>
            <Input
                inputMode={"text"}
                colorScheme={"primary"}
                variant='filled'
                _placeholder={{
                    fontSize: 14
                }}
                defaultValue={skuOld}
                errorBorderColor={"error.700"}
                disabled={isSubmitting}
                type={"text"}
                isInvalid={!!errors.sku}
                focusBorderColor={!!errors.sku ? "error.700" : "primary.400"}
                {...register('sku')}
                placeholder='Es. COD-00A1'/>
        </AddTableRow>;
    }

    function getBarcodeRow() {
        return <AddTableRow name={"Barcode"}>
            <Input
                inputMode={"text"}
                colorScheme={"primary"}
                variant='filled'
                _placeholder={{fontSize: 14}}
                defaultValue={barcode}
                errorBorderColor={"error.700"}
                disabled={isSubmitting}
                type={"text"}
                isInvalid={!!errors.barcode}
                focusBorderColor={!!errors.barcode ? "error.700" : "primary.400"}
                {...register('barcode')}
                placeholder='Es. 424234786421'/>
        </AddTableRow>;
    }

    function getLabelsRow() {
        const options = [
            {value: "senza_glutine", label: "Senza Glutine"},
            {value: "biologico", label: "Biologico"},
            {value: "vegano", label: "Vegano"},
            {value: "imballaggio_riciclato", label: "Imballaggio riciclato e/o riciclabile"},
            {value: "prodotto_siciliano", label: "Prodotto Siciliano"},
        ];

        const handleChange = (selected: MultiValue<OptionType>) => {
            setSelectedLabels([...selected]);
        };

        function getLogo(name: string, size: number = 48) {
            switch (name) {
                case "senza_glutine":
                    return <GlutenFreeLogo width={size} height={size}/>
                case "biologico":
                    return <BioLogo width={size} height={size}/>
                case "vegano":
                    return <VeganLogo width={size} height={size}/>
                case "imballaggio_riciclato":
                    return <Recycled width={size} height={size}/>
                case "prodotto_siciliano":
                    return <SicilyLogo width={size} height={size}/>
            }
        }

        const CustomSingleValue = ({data, ...props}: any) => (
            <components.SingleValue {...props}>
                <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                    {/*<img src={data.imageUrl} alt={data.label} width={24} height={24} style={{ borderRadius: "50%" }} />*/}
                    {getLogo(data.value, 48)}
                    {data.label}
                </div>
            </components.SingleValue>
        );

        const CustomOption = (props: any) => {
            const {data, innerRef, innerProps} = props;
            return (
                <div ref={innerRef} {...innerProps}
                     style={{display: "flex", alignItems: "center", padding: "8px", gap: "8px"}}>
                    {/*<img src={data.imageUrl} alt={data.label} width={48} height={48} style={{ borderRadius: "8px" }} />*/}
                    {getLogo(data.value, 24)}
                    {data.label}
                </div>
            );
        };

        return <AddTableRow name={"Etichette"}>
            <Select
                isMulti
                options={options}
                value={selectedLabels}
                onChange={handleChange}
                placeholder="Seleziona..."
                components={{Option: CustomOption, SingleValue: CustomSingleValue}}
            />
        </AddTableRow>;
    }


    function getProducerRowNew() {
        return <AddTableRow name={"Produttore (*)"}>
            <VStack alignItems={"flex-start"} w={"100%"}>
                <Stack w={"100%"}>
                    <Select
                        options={producerOptions}
                        value={chooseProducer}
                        onChange={handleProducerChange}
                        getOptionLabel={(e) => e.label}
                        getOptionValue={(e) => e.value}
                        placeholder="Select producer..."
                    />
                    {/*<Select*/}
                    {/*    isMulti*/}
                    {/*    options={producerOptions}*/}
                    {/*    value={chooseProducer}*/}
                    {/*    onChange={(selectedOptions: MultiValue<OptionType>) =>*/}
                    {/*        handleProducerChange([...selectedOptions])*/}
                    {/*    }*/}
                    {/*    placeholder="Seleziona la categoria..."*/}
                    {/*/>*/}
                </Stack>
            </VStack>
        </AddTableRow>
    }

    function getCategoryRow() {
        return <AddTableRow name={"Categoria"}>
            <VStack alignItems={"flex-start"} w={"100%"}>
                <Stack w={"100%"}>
                    <Select
                        isMulti
                        options={options}
                        value={chooseCategories}
                        onChange={(selectedOptions: MultiValue<OptionType>) =>
                            setChooseCategories([...selectedOptions])
                        }
                        placeholder="Seleziona la categoria..."
                    />
                </Stack>
            </VStack>
        </AddTableRow>;
    }

    function getDescriptionRow() {
        return <AddTableRow name={"Descrizione (*)"}>
            <VStack alignItems={"flex-start"} w={"100%"}>
                <ReactQuill
                    theme="snow"
                    modules={{toolbar: toolbarOptions}}
                    value={valueQuill}
                    onChange={value => {
                        setValue("description", value);
                        setValueQuill(value)
                    }}
                />
                <Text fontSize={"14px"} color={"rgba(0,0,0,0.6)"}>Scrivi una descrizione completa.
                    Verrà mostrata nella pagina di dettaglio del prodotto.</Text>
            </VStack>
        </AddTableRow>
    }

    function getShortDescriptionRow() {
        return <AddTableRow name={"Descrizione breve"}>
            <VStack alignItems={"flex-start"} w={"100%"}>
                <ReactQuill theme="snow" modules={{toolbar: toolbarOptions}} value={valueShortQuill}
                            onChange={value => {
                                setValue("shortDescription", value);
                                setValueShortQuill(value);
                            }}/>
                <Text fontSize={"14px"} color={"rgba(0,0,0,0.6)"}>Scrivi una descrizione breve.
                    Verrà mostrata nell'anteprima del prodotto.</Text>
            </VStack>
        </AddTableRow>;
    }

    function getAttributesRow() {
        return <AddTableRow name={"Attributi"}>
            <KeyValueSection hintKey={"Nome attributo"}
                             addAttribute={addAttribute}
                             removeAttribute={removeAttribute}
                             setNewAttribute={setNewAttribute}
                             attributeMap={attributes}/>
        </AddTableRow>
    }

    function getVariantRow() {
        return <AddTableRow name={"Varianti"}>
            <VStack w={'100%'}>
                {variants.filter(value => !value.isDeleted).map((value, index) => {
                    return <VStack w={"100%"}>
                        <HStack w={"100%"} key={`input_${index}`}>
                            <Text fontSize={14} fontWeight={'bold'}>{index + 1}</Text>
                            <Input
                                inputMode={"text"}
                                colorScheme={"primary"}
                                variant='filled'
                                value={value.value}
                                onChange={event => updateVariant(value.id, {
                                    ...value, value: event.target.value
                                })}
                                _placeholder={{fontSize: 14}}
                                errorBorderColor={"error.700"}
                                type={"text"}
                                placeholder='Valore'/>
                            <IconButton icon={<DeleteIcon/>} aria-label={"delete"}
                                        onClick={() => removeVariant(value.id)}/>
                        </HStack>
                    </VStack>
                })}
                <Button
                    colorScheme="green"
                    w={"100%"}
                    h={"24px"}
                    leftIcon={<AddIcon w={"8px"} h={"8px"}/>}
                    onClick={() => {
                        addVariant({id: (Date.now() + Math.random())});
                    }}><Text fontSize={12}>Valore</Text></Button>
            </VStack>
        </AddTableRow>
    }

    function getMainImageRow() {
        return <AddTableRow name={"Immagine"}>
            <ImageSelectorComponent
                setImageModel={setMainImage}
                imageModel={mainImage}
                isMain={true}
                handleCloseButtonClick={(model) => {
                    if (id) setImageToDelete([...(imageToDelete || []), model?.id || ""]);
                    setMainImage(undefined);
                }}
                description={"Seleziona l'immagine principale del prodotto, verrà mostrata nell'anteprima."}/>
        </AddTableRow>;
    }

    function getImagesRow() {
        return <AddTableRow name={"Media"}>
            <MediaSelectorComponent
                setImages={setImages}
                images={images}
                handleCloseButtonClick={(model) => {
                    if (id) setImageToDelete([...(imageToDelete || []), model?.id || ""]);
                }}
                description={"Seleziona le foto, i video o gli audio che vorresti utilizzare per descrivere al meglio il tuo prodotto."}
            />
        </AddTableRow>;
    }

    return <VStack id={"container"} w={"100%"} h={"100%"} alignItems={"center"}>
        <HStack cursor={"pointer"} h={"64px"} px={"24px"} borderRadius={"24px"} position={"absolute"}
                alignItems={"center"} zIndex={5}
                justifyContent={"center"} bottom={"24px"} right={"48px"}
                backgroundColor={isSubmitting ? "primary.100" : "secondary.200"}
                _hover={{backgroundColor: "tertiary.200"}} onClick={handleSubmit(onSubmit)}>
            {isSubmitting ? <Spinner width={"24px"} height={"24px"}/> :
                <CheckCircleIcon w={"24px"} h={"24px"} color={"primary.400"} alignSelf={"center"}/>}
            <Text fontWeight={"bold"} color={"primary.400"}>Conferma</Text>
        </HStack>

        <VStack justifyContent={"center"} flex={1} alignItems={"center"} overflowY={"hidden"}>
            <HStack w={"100%"} alignItems={"center"} pt={"16px"} justifyContent={"space-between"} ms={"24px"}>
                <HStack alignItems={"center"} p={"16px"} _hover={{
                    backgroundColor: "tertiary.200",
                    borderRadius: "16px"
                }} cursor={"pointer"} onClick={() => {
                    navigate("/products", {replace: true});
                }}>
                    <ArrowBackIcon/>
                    <Text fontWeight={"bold"}>Indietro</Text>
                </HStack>
            </HStack>
            <Text fontSize={"24px"} justifySelf={"center"} fontWeight={"bold"}
                  ms={"24px"}>{id ? nameOld : "Nuovo prodotto"}</Text>
            <TableContainer w={"100%"} alignItems={"center"} whiteSpace={"pre-line"} mr={"16px"} overflowY={"scroll"}
                            css={scrollbarCss({marginRight: 48})}>
                <Table variant={"none"} w={"70%"} mx={"auto"}>
                    <Tbody>
                        {getNameRow()}
                        {userType === 'admin' && getProducerRowNew()}
                        {/*{getSwitchRow()}*/}
                        {getSkuRow()}
                        {getBarcodeRow()}
                        {getLabelsRow()}
                        {getCategoryRow()}
                        {getDescriptionRow()}
                        {getShortDescriptionRow()}
                        {getAttributesRow()}
                        {getVariantRow()}
                        {getMainImageRow()}
                        {getImagesRow()}
                    </Tbody>
                </Table>
            </TableContainer>
        </VStack>
    </VStack>
}

type KeyValueMap = Map<string, string>;
type KeyValueSectionMap = Map<string, KeyValueSectionModel>;

interface KeyValueSectionModel {
    key: string,
    values: string[]
}

const KeyValueSection = ({hintKey, attributeMap, addAttribute, removeAttribute, setNewAttribute}: {
    hintKey: string,
    attributeMap: KeyValueSectionMap,
    addAttribute: (key: string, value: KeyValueSectionModel) => void,
    removeAttribute: (key: string) => void,
    setNewAttribute: (key: string, value: KeyValueSectionModel) => void
}) => {
    return <VStack alignItems={'flex-start'}>
        ...{Array.from(attributeMap).map(([key, value], index) => {
            const {key: modelKey, values} = value;
            return <VStack w={'100%'} key={`key_${key}_${index}`}>
                <HStack w={'100%'}>
                    <Input
                        inputMode={"text"}
                        colorScheme={"primary"}
                        variant='filled'
                        value={modelKey}
                        onChange={event => setNewAttribute(key, {
                            key: event.target.value,
                            values: values
                        })}
                        _placeholder={{fontSize: 14}}
                        errorBorderColor={"error.700"}
                        type={"text"}
                        placeholder={hintKey}/>
                    <IconButton icon={<DeleteIcon color={'darkred'}/>} aria-label={"delete"}
                                onClick={() => removeAttribute(key)}/>
                </HStack>
                <KeyValueInput defaultModel={value}
                               onChangeList={(values) => {
                                   setNewAttribute(key, {
                                       key: modelKey,
                                       values
                                   })
                               }}/>
            </VStack>
        }
    )}
        <Button
            colorScheme="teal"
            w={"100%"}
            h={"36px"}
            leftIcon={<AddIcon w={"10px"} h={"10px"}/>}
            onClick={() => {
                addAttribute(Date.now().toString(), {
                    key: "",
                    values: []
                });
            }}><Text fontSize={12}>Attibuto</Text>
        </Button>

        <Stack mt={"8px"} h={"1px"} w={'100%'} backgroundColor={'rgba(0,0,0,0.2)'}/>
    </VStack>
}

const KeyValueInput = ({onChangeList, defaultModel}: {
    onChangeList: (values: string[]) => void,
    defaultModel?: KeyValueSectionModel
}) => {
    const [values, setValues] = useState<KeyValueMap>(defaultModel ? new Map() : new Map().set(Date.now().toString(), ""));

    const addElement = (key: string, value: string) => {
        setValues(new Map(values.set(`${key}_${Math.random()}`, value)));
    };

    const removeElement = (key: string) => {
        const newMap = new Map(values);
        newMap.delete(key);
        setValues(newMap);
    };

    const setNewValue = (key: string, value: string) => {
        const newMap = new Map(values);
        newMap.set(key, value);
        setValues(newMap);
    }

    useEffect(() => {
        onChangeList(Array.from(values).map(([key, value]) => value));
    }, [values]);

    useEffect(() => {
        defaultModel?.values.forEach((value, index) => addElement(`${Date.now().toString()}_${index}`, value));
    }, []);

    return <VStack alignItems={'flex-start'} w={"100%"}>
        {Array.from(values).map(([key, value], index) =>
            <HStack w={"100%"} key={`input_${index}`}>
                <Text fontSize={14} fontWeight={'bold'}>{index + 1}</Text>
                <Input
                    inputMode={"text"}
                    colorScheme={"primary"}
                    variant='filled'
                    value={value}
                    onChange={event => setNewValue(key, event.target.value)}
                    _placeholder={{fontSize: 14}}
                    errorBorderColor={"error.700"}
                    type={"text"}
                    placeholder='Valore'/>
                <IconButton icon={<DeleteIcon/>} aria-label={"delete"} onClick={() => removeElement(key)}/>
            </HStack>
        )}
        <Button
            colorScheme="green"
            w={"100%"}
            h={"24px"}
            leftIcon={<AddIcon w={"8px"} h={"8px"}/>}
            onClick={() => {
                addElement(Date.now().toString(), "");
            }}><Text fontSize={12}>Valore</Text></Button>
    </VStack>
}

function transformToKeyValueSectionMap(data: string): Map<string, KeyValueSectionModel> {
    try {
        const parsedData = JSON.parse(data);
        if (!Array.isArray(parsedData)) {
            throw new Error("Input data must be an array of objects.");
        }

        const map = new Map<string, KeyValueSectionModel>();

        parsedData.forEach(item => {
            if (typeof item.key === "string" && Array.isArray(item.values)) {
                map.set(item.key, {key: item.key, values: item.values});
            }
        });

        return map;
    } catch (error) {
        return new Map<string, KeyValueSectionModel>();
    }
}

function getLabelNameByValue(value: string) {
    switch (value) {
        case "senza_glutine":
            return "Senza Glutine";
        case "biologico":
            return "Biologico";
        case "vegano":
            return "Vegano";
        case "imballaggio_riciclato":
            return "Imballaggio riciclato e/o riciclabile";
        case "prodotto_siciliano":
            return "Prodotto Siciliano";
    }
}
